import { Box, alpha, styled } from '@mui/material';
import { useIntl } from 'react-intl';
import {
    OperationPreviewDisplay,
    useOperationPreviewDisplay,
} from '~/hooks/viewer/useOperationPreviewDisplay';

/** A status line, not a control: it takes no pointer events. */
const Banner = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    columnGap: theme.spacing(0.75),
    maxWidth: 560,
    padding: theme.spacing(0.5, 1.5, 0.5, 1.25),
    ...theme.mixins.floatingSurface,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
    ...theme.typography.caption,
    // The glass surface is dark in both themes, so the text never follows
    // the palette.
    color: 'rgba(255, 255, 255, 0.85)',
    userSelect: 'none',
    pointerEvents: 'none',
}));

const Dot = styled('span')(({ theme }) => ({
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.2)}`,
}));

const Title = styled('span')(({ theme }) => ({
    color: theme.palette.primary.main,
    fontWeight: 600,
}));

/** One phrase of the banner; phrases wrap as wholes on a narrow viewport. */
const Phrase = styled('span')({ whiteSpace: 'nowrap' });

const Separator = () => <span aria-hidden>·</span>;

/** What the preview does to the rest of the display, if anything. */
function previewEffect({
    dimsOtherStructures,
    hidesPreviewedStructure,
}: OperationPreviewDisplay):
    | 'dimmed'
    | 'hidden'
    | 'hiddenAndDimmed'
    | undefined {
    if (dimsOtherStructures && hidesPreviewedStructure) {
        return 'hiddenAndDimmed';
    }
    if (dimsOtherStructures) {
        return 'dimmed';
    }
    if (hidesPreviewedStructure) {
        return 'hidden';
    }
    return undefined;
}

/**
 * States, while an operation preview is on screen, that it is a preview,
 * that it is not applied, and what it does to the rest of the display while
 * it lasts. Takes no pointer events. Renders nothing while no preview is on
 * screen.
 */
export default function OperationPreviewIndicator() {
    const intl = useIntl();
    const preview = useOperationPreviewDisplay();
    if (!preview) {
        return null;
    }
    const effect = previewEffect(preview);
    return (
        <Banner data-testid="operation-preview-indicator" role="status">
            <Dot aria-hidden />
            <Title>
                {intl.formatMessage({ id: 'operationPreview.title' })}
            </Title>
            <Separator />
            <Phrase>
                {intl.formatMessage({ id: 'operationPreview.notApplied' })}
            </Phrase>
            {effect && (
                <>
                    <Separator />
                    <Phrase data-testid="operation-preview-effect">
                        {intl.formatMessage(
                            { id: 'operationPreview.effect' },
                            { effect }
                        )}
                    </Phrase>
                </>
            )}
        </Banner>
    );
}
